/**
 * BenchmarkFeedbackPanel — Joan's report-review tool (Layer 2 free-text).
 *
 * Joan types a plain-English critique; the server interprets it into proposed
 * structured actions (exclude a defunct/non-supplier entity, merge a sub-brand
 * into its parent, re-map a source). Nothing is applied until she clicks Apply
 * (confirm-before-apply — a safety on the AI's write, not a gate on her input).
 * Applying updates the global rules and refreshes the report.
 *
 * Layer 1 (one-click structured flags on the report tables) lives in
 * BenchmarkDetail and posts the same feedback items, applied immediately since
 * the click itself is the confirmation.
 */
import { useCallback, useEffect, useState } from 'react';
import type { BenchmarkFeedback, FeedbackAction } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

function actionLabel(a: FeedbackAction): string {
  return a.description || a.type;
}

function ActionRow({ a }: { a: FeedbackAction }): JSX.Element {
  return (
    <li className={a.applicable ? undefined : 'muted'}>
      <span className="pill pill-pending">{a.type.replace(/_/g, ' ')}</span>{' '}
      {actionLabel(a)}
      {!a.applicable && (
        <em> — logged only (needs a re-run or human review)</em>
      )}
    </li>
  );
}

export function BenchmarkFeedbackPanel({
  runId,
  onApplied,
}: {
  runId: string;
  onApplied: () => void;
}): JSX.Element {
  const [items, setItems] = useState<BenchmarkFeedback[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.listBenchmarkFeedback(runId);
      setItems(r.feedback);
    } catch {
      /* non-fatal — panel just shows nothing */
    }
  }, [runId]);
  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (): Promise<void> => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.createBenchmarkFeedback(runId, {
        mode: 'freetext',
        rawInput: text.trim(),
      });
      setText('');
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to submit');
    } finally {
      setBusy(false);
    }
  };

  const apply = async (fid: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.applyBenchmarkFeedback(runId, fid);
      const n = r.applied.length;
      setNotice(
        n > 0
          ? `Applied ${n} change${n === 1 ? '' : 's'} and refreshed the report.`
          : 'Nothing to apply (logged for review).',
      );
      if (r.warnings.length > 0) setError(r.warnings.join('; '));
      await load();
      if (n > 0) onApplied();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (fid: string): Promise<void> => {
    setBusy(true);
    try {
      await api.dismissBenchmarkFeedback(runId, fid);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const open = items.filter((i) => i.status === 'open');
  const past = items.filter((i) => i.status !== 'open');

  return (
    <div className="card feedback-panel">
      <h2 className="section-head">
        <span>Review this report</span>
      </h2>
      <p className="muted">
        Describe anything that looks wrong — a defunct or non-supplier entity, a
        company listed under the wrong name, a mis-categorized source. The
        assistant proposes concrete fixes; you confirm before anything changes.
      </p>
      <textarea
        rows={3}
        value={text}
        placeholder="e.g. ELISA Genie isn't a competitor — drop it. And 'Sigma' should roll up under MilliporeSigma."
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
      />
      <div className="head-actions" style={{ marginTop: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void submit()}
          disabled={busy || !text.trim()}
        >
          {busy ? 'Working…' : 'Interpret feedback'}
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      {open.length > 0 && (
        <>
          <h3>Proposed changes — confirm to apply</h3>
          {open.map((item) => (
            <div key={item.id} className="feedback-item">
              {item.rawInput && (
                <p className="feedback-quote">“{item.rawInput}”</p>
              )}
              {item.proposedActions.length > 0 ? (
                <ul className="feedback-actions">
                  {item.proposedActions.map((a, i) => (
                    <ActionRow key={i} a={a} />
                  ))}
                </ul>
              ) : (
                <p className="muted">No actionable changes proposed.</p>
              )}
              <div className="head-actions">
                {item.proposedActions.some((a) => a.applicable) && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void apply(item.id)}
                    disabled={busy}
                  >
                    Apply
                  </button>
                )}
                <button
                  type="button"
                  className="btn"
                  onClick={() => void dismiss(item.id)}
                  disabled={busy}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {past.length > 0 && (
        <>
          <h3 className="muted">History</h3>
          <ul className="feedback-history">
            {past.map((item) => (
              <li key={item.id}>
                <span
                  className={
                    item.status === 'applied'
                      ? 'pill pill-complete'
                      : 'pill pill-draft'
                  }
                >
                  {item.status}
                </span>{' '}
                {item.rawInput ??
                  item.note ??
                  item.appliedActions.map(actionLabel).join('; ') ??
                  '—'}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
